import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, MessageSquare, Send, Trash2, Calendar as CalIcon, User, Mail, Anchor, KeyRound, UserPlus, Copy, Plus, Gift, CreditCard, LogIn, LogOut, ClipboardCheck } from "lucide-react";
import DocumentUploader, { DocumentRow } from "../components/DocumentUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import AdminTimeChangeCard from "../components/AdminTimeChangeCard";
import AdminBoatInspectionCard from "../components/AdminBoatInspectionCard";
import BookingPaymentsCard from "../components/BookingPaymentsCard";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bk, setBk] = useState(null);
  const [boats, setBoats] = useState([]);
  const [draft, setDraft] = useState({});
  const [savingFields, setSavingFields] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionEmail, setProvisionEmail] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [includedDialogOpen, setIncludedDialogOpen] = useState(false);
  const [includedDraft, setIncludedDraft] = useState({}); // { productId: quantity }
  const [savingIncluded, setSavingIncluded] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveDraft, setMoveDraft] = useState({ boat_id: "", move_reason: "" });
  const [movingBoat, setMovingBoat] = useState(false);
  const [payLinkOpen, setPayLinkOpen] = useState(false);
  const [payLink, setPayLink] = useState({
    description: "", amount_eur: "", stripe_payment_url: "",
  });
  const [payLinkBusy, setPayLinkBusy] = useState(false);
  const [arrivalDue, setArrivalDue] = useState({
    arrival_due_amount: "",
    arrival_due_paid: false,
    arrival_due_note: "",
  });

  const load = async () => {
    const [d, bs, ords, prods] = await Promise.all([
      api.get(`/bookings/${id}`),
      api.get("/boats"),
      api.get(`/orders?booking_id=${id}`).catch(() => ({ data: [] })),
      api.get("/products").catch(() => ({ data: [] })),
    ]);
    setBk(d.data);
    setBoats(bs.data);
    setOrders(ords.data || []);
    setProducts((prods.data || []).filter((p) => p.active));
    setDraft({
      customer_name: d.data.customer_name || "",
      customer_email: d.data.customer_email || "",
      num_guests: d.data.num_guests || 1,
      start_date: (d.data.start_date || "").slice(0, 10),
      end_date: (d.data.end_date || "").slice(0, 10),
      notes: d.data.notes || "",
      checkin_time: d.data.checkin_time || "",
      checkout_time: d.data.checkout_time || "",
    });
    setArrivalDue({
      arrival_due_amount: d.data.arrival_due_amount ?? "",
      arrival_due_paid: !!d.data.arrival_due_paid,
      arrival_due_note: d.data.arrival_due_note || "",
    });
  };

  useEffect(() => { load(); }, [id]);

  const saveFields = async () => {
    setSavingFields(true);
    try {
      await api.patch(`/bookings/${id}`, draft);
      toast.success("Booking oppdatert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSavingFields(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/bookings/${id}/notes`, { body: newNote.trim() });
      setNewNote("");
      toast.success("Notat lagt til");
      await load();
    } catch { toast.error("Kunne ikke legge til notat"); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Slette dette notatet?")) return;
    await api.delete(`/bookings/${id}/notes/${noteId}`);
    await load();
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Slette dokumentet?")) return;
    await api.delete(`/documents/${docId}`);
    toast.success("Slettet");
    await load();
  };

  const deleteBooking = async () => {
    if (!window.confirm("Slette hele bookingen? Dette kan ikke angres.")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Slettet");
    navigate("/admin/bookings");
  };

  const provisionCustomer = async () => {
    try {
      const body = provisionEmail ? { email: provisionEmail } : {};
      const r = await api.post(`/bookings/${id}/provision-customer`, body);
      if (r.data.auto_credentials) {
        setCredentials(r.data.auto_credentials);
        toast.success("Kundeprofil opprettet");
      } else {
        toast.success("Booking koblet til eksisterende kundekonto");
      }
      setProvisionOpen(false);
      setProvisionEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette profil");
    }
  };

  const copyCredentials = () => {
    if (!credentials) return;
    const text = `E-post: ${credentials.email}\nPassord: ${credentials.password}\n\n${credentials.note}`;
    try {
      navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const saveArrivalDue = async () => {
    try {
      await api.patch(`/bookings/${id}`, {
        arrival_due_amount: arrivalDue.arrival_due_amount === ""
          ? 0
          : parseInt(arrivalDue.arrival_due_amount, 10) || 0,
        arrival_due_paid: arrivalDue.arrival_due_paid,
        arrival_due_note: arrivalDue.arrival_due_note,
      });
      toast.success("Lagret");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  const markOrderPaid = async (orderId) => {
    if (!window.confirm("Marker denne bestillingen som betalt?")) return;
    try {
      await api.patch(`/orders/${orderId}/mark-paid`, { payment_method: "cash" });
      toast.success("Bestilling markert som betalt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke markere");
    }
  };

  const saveIncluded = async () => {
    // Build items list from quantities. Skip 0/blank entries.
    const items = Object.entries(includedDraft)
      .map(([product_id, q]) => ({ product_id, quantity: parseInt(q, 10) || 0 }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }
    setSavingIncluded(true);
    try {
      await api.post("/orders", {
        booking_id: id,
        items,
        included_with_booking: true,
        note: "Lagt til av admin – inkludert i booking",
      });
      toast.success("Inkluderte produkter lagret – kunden ser dette ved innlogging");
      setIncludedDialogOpen(false);
      setIncludedDraft({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSavingIncluded(false);
    }
  };

  const submitPayLink = async () => {
    const url = payLink.stripe_payment_url.trim();
    const desc = payLink.description.trim();
    const amount = parseFloat(payLink.amount_eur);
    if (!desc) return toast.error("Beskrivelse mangler");
    if (!amount || amount <= 0) return toast.error("Ugyldig beløp");
    if (!url.includes("stripe.com")) return toast.error("URL må være en Stripe-lenke");
    setPayLinkBusy(true);
    try {
      await api.post("/admin/payment-links", {
        booking_id: id, description: desc, amount_eur: amount, stripe_payment_url: url,
      });
      toast.success("Betalingslink sendt til kunden");
      setPayLinkOpen(false);
      setPayLink({ description: "", amount_eur: "", stripe_payment_url: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setPayLinkBusy(false);
    }
  };

  const moveBoat = async () => {
    const target = moveDraft.boat_id;
    if (!target || target === bk.boat_id) {
      toast.error("Velg en annen båt enn nåværende");
      return;
    }
    setMovingBoat(true);
    try {
      const { data } = await api.patch(`/bookings/${id}`, {
        boat_id: target,
        move_reason: (moveDraft.move_reason || "").trim(),
      });
      if (data._moved_warning) {
        toast.warning(data._moved_warning, { duration: 7000 });
      } else {
        toast.success("Bookingen er flyttet til ny båt");
      }
      setMoveDialogOpen(false);
      setMoveDraft({ boat_id: "", move_reason: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke flytte");
    } finally {
      setMovingBoat(false);
    }
  };

  const removeIncluded = async (orderId) => {
    if (!window.confirm("Fjern denne posten fra inkludert-i-booking?")) return;
    try {
      await api.patch(`/orders/${orderId}/cancel`);
      toast.success("Fjernet");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke fjerne");
    }
  };

  const cancelPendingOrder = async (orderId, hasStripeSession) => {
    const msg = hasStripeSession
      ? "Kansellere denne ubetalte bestillingen? Den åpne Stripe-siden vil utløpe så kunden ikke kan betale."
      : "Kansellere denne ubetalte bestillingen?";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.patch(`/orders/${orderId}/cancel`);
      if (r.data?.stripe_session_expired) {
        toast.success("Kansellert · Stripe-sesjonen utløpt");
      } else if (hasStripeSession) {
        toast.warning("Kansellert lokalt, men Stripe-sesjonen kunne ikke utløpes. Sjekk i Stripe Dashboard.");
      } else {
        toast.success("Kansellert");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke kansellere");
    }
  };

  if (!bk) return null;
  const isAdmin = user.role === "admin";
  const notes = bk.internal_notes || [];
  // Split orders for separate display: "included with booking" vs regular extras.
  const includedOrders = orders.filter(
    (o) => o.included_with_booking && o.status !== "cancelled"
  );
  const extraOrders = orders.filter((o) => !o.included_with_booking);
  const insuranceOrder = orders.find(
    (o) => o.status !== "cancelled" && (o.items || []).some((i) => i.category === "insurance"),
  );

  const markInsurancePrepaid = async (insuranceType) => {
    const label = insuranceType === "deposit" ? "depositum (3000 €)" : "skadefritak (460 €)";
    if (!window.confirm(`Registrere ${label} som allerede betalt for denne bookingen?`)) return;
    try {
      await api.post("/admin/insurance-prepaid", {
        booking_id: id, insurance_type: insuranceType,
      });
      toast.success("Forsikring registrert som forhåndsbetalt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke registrere");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/bookings"
              className="inline-flex items-center gap-2 text-sm text-[#2B3F4D] hover:text-[#0C1824]">
          <ArrowLeft className="w-4 h-4" /> Tilbake til bookinger
        </Link>
        {isAdmin && (
          <Button variant="outline" onClick={deleteBooking}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="booking-delete">
            <Trash2 className="w-4 h-4 mr-1.5" /> Slett booking
          </Button>
        )}
      </div>

      <Card className="p-6 sm:p-8 bg-gradient-to-br from-[#0C1824] to-[#11283A] text-white">
        <div className="text-xs uppercase tracking-[0.32em] text-[#ECC974]">Booking</div>
        <div className="flex items-baseline justify-between gap-4 flex-wrap mt-1">
          <h1 className="text-2xl sm:text-3xl font-bold">{bk.customer_name}</h1>
          <Badge className="bg-[#BD984D]/20 text-[#ECC974] border-0">
            {bk.boat?.name || "Båt slettet"}
          </Badge>
        </div>
        <div className="text-sm text-white/80 mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
          <span><CalIcon className="w-3.5 h-3.5 inline -mt-1 mr-1.5" />
            {bk.start_date?.slice(0, 10)} → {bk.end_date?.slice(0, 10)}
          </span>
          <span><User className="w-3.5 h-3.5 inline -mt-1 mr-1.5" />
            {bk.num_guests} pers.
          </span>
          {bk.customer_email && (
            <span><Mail className="w-3.5 h-3.5 inline -mt-1 mr-1.5" />
              {bk.customer_email}
            </span>
          )}
        </div>
        {isAdmin && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setMoveDraft({ boat_id: bk.boat_id || "", move_reason: "" });
                setMoveDialogOpen(true);
              }}
              data-testid="booking-move-boat"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-8"
            >
              <Anchor className="w-3.5 h-3.5 mr-1.5" />
              Flytt til annen båt
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPayLinkOpen(true)}
              data-testid="booking-send-payment-link"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-8"
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
              Send betalingslink
            </Button>
          </div>
        )}
      </Card>

      {/* Forsikring – admin kan registrere som forhåndsbetalt */}
      {isAdmin && (
        <Card className={`p-4 border-l-4 ${insuranceOrder ? "border-l-emerald-500" : "border-l-[#BD984D]"}`}
              data-testid="booking-insurance-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">Forsikring</div>
              {insuranceOrder ? (
                <>
                  <div className="text-sm font-semibold text-[#0C1824] mt-1">
                    {insuranceOrder.items[0].name_no}
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-1">
                    {insuranceOrder.status === "paid" ? (
                      <span className="text-emerald-700 font-semibold">
                        Betalt · {insuranceOrder.payment_method === "prepaid"
                          ? `forhåndsbetalt (registrert av ${insuranceOrder.paid_by_name})`
                          : insuranceOrder.payment_method === "stripe" ? "via Stripe"
                          : insuranceOrder.payment_method || "manuelt"}
                      </span>
                    ) : (
                      <span className="text-[#A57F39]">Valgt, avventer betaling</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-[#0C1824] mt-1">
                    Ikke valgt enda
                  </div>
                  <p className="text-xs text-[#2B3F4D] mt-1">
                    Kunden må velge depositum (3000 €) eller skadefritak (460 €) før avgang.
                    Hvis kunden har betalt i forkant (kontant, Vipps, faktura …), kan du
                    registrere det her.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm"
                            onClick={() => markInsurancePrepaid("deposit")}
                            data-testid="insurance-prepaid-deposit"
                            className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                      Depositum betalt (3000 €)
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => markInsurancePrepaid("waiver")}
                            data-testid="insurance-prepaid-waiver"
                            className="border-[#BD984D]/60 text-[#A57F39] hover:bg-[#BD984D]/10">
                      Skadefritak betalt (460 €)
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      )}
      {/* Customer profile banner – nudges admin to provision an account
          for bookings that don't have one yet. */}
      {isAdmin && !bk.customer_user_id && (
        <Card
          className="p-4 bg-[#BD984D]/10 border-l-4 border-l-[#BD984D]"
          data-testid="booking-provision-banner"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <UserPlus className="w-5 h-5 text-[#A57F39] mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-[#0C1824]">
                  Ingen kundeprofil opprettet ennå
                </div>
                <p className="text-sm text-[#2B3F4D] mt-0.5 max-w-xl">
                  Opprett en kunde-innlogging slik at kunden får tilgang til
                  egen portal, dokumenter og kan rapportere hendelser. Passord
                  blir innsjekksdato (DDMMYYYY).
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setProvisionEmail(bk.customer_email || "");
                setProvisionOpen(true);
              }}
              data-testid="booking-provision-open"
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Opprett kundeprofil
            </Button>
          </div>
        </Card>
      )}

      <Dialog open={provisionOpen} onOpenChange={setProvisionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Opprett kundeprofil</DialogTitle>
            <DialogDescription>
              {bk.customer_email
                ? `Vi oppretter en kundekonto knyttet til ${bk.customer_email}. Passordet blir automatisk satt til innsjekksdato (DDMMYYYY).`
                : "Skriv inn e-postadresse til kunden. Passordet blir automatisk satt til innsjekksdato (DDMMYYYY)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>E-post</Label>
              <Input
                type="email"
                value={provisionEmail}
                onChange={(e) => setProvisionEmail(e.target.value)}
                data-testid="booking-provision-email"
                placeholder="kunde@eksempel.no"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProvisionOpen(false)}>Avbryt</Button>
            <Button
              onClick={provisionCustomer}
              disabled={!provisionEmail.trim()}
              data-testid="booking-provision-submit"
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Opprett profil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!credentials} onOpenChange={(o) => !o && setCredentials(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#BD984D]" />
              Påloggingsdetaljer
            </DialogTitle>
            <DialogDescription>
              Send disse til kunden. Passordet vises kun nå – etter at du
              lukker dialogen kan du sette et nytt fra «Brukere».
            </DialogDescription>
          </DialogHeader>
          {credentials && (
            <div className="space-y-3" data-testid="booking-credentials">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm">
                <div>E-post: <strong>{credentials.email}</strong></div>
                <div>Passord: <strong>{credentials.password}</strong></div>
              </div>
              <p className="text-xs text-[#2B3F4D]">{credentials.note}</p>
              <Button
                onClick={copyCredentials}
                data-testid="booking-credentials-copy"
                className="w-full bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
              >
                {copied ? <>Kopiert!</> : <><Copy className="w-4 h-4 mr-1.5" /> Kopier til utklippstavle</>}
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentials(null)}>Lukk</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Betales ved ankomst + tilleggsbestillinger */}
      <Card className="p-5 space-y-4" data-testid="booking-payments">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-[#0C1824]">Betaling</h2>
          <Badge className={
            arrivalDue.arrival_due_paid
              ? "bg-emerald-100 text-emerald-700"
              : "bg-[#BD984D]/15 text-[#A57F39]"
          }>
            {arrivalDue.arrival_due_paid ? "Betalt" : "Forfaller ved ankomst"}
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label>Betales ved ankomst (€)</Label>
            <Input
              type="number"
              min={0}
              value={arrivalDue.arrival_due_amount}
              onChange={(e) => setArrivalDue({ ...arrivalDue, arrival_due_amount: e.target.value })}
              data-testid="booking-arrival-due-amount"
              placeholder="0"
            />
          </div>
          <div>
            <Label>Notat (vises i kundens portal)</Label>
            <Input
              value={arrivalDue.arrival_due_note}
              onChange={(e) => setArrivalDue({ ...arrivalDue, arrival_due_note: e.target.value })}
              data-testid="booking-arrival-due-note"
              placeholder="F.eks. «Depositum 200 + balanse 150»"
            />
          </div>
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 cursor-pointer"
                 data-testid="booking-arrival-due-paid-label">
            <input
              type="checkbox"
              checked={arrivalDue.arrival_due_paid}
              onChange={(e) => setArrivalDue({ ...arrivalDue, arrival_due_paid: e.target.checked })}
              data-testid="booking-arrival-due-paid"
              className="accent-[#BD984D]"
            />
            <span className="text-sm">Betalt</span>
          </label>
        </div>
        <Button
          onClick={saveArrivalDue}
          data-testid="booking-arrival-due-save"
          className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
        >
          Lagre betalingsinfo
        </Button>

        {/* Included in booking – admin pre-loads paid-for products so the
            customer sees what's covered when they log in. */}
        {isAdmin && (
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-[#1DA3B3]" />
                <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                  Inkludert i booking ({includedOrders.length})
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setIncludedDialogOpen(true)}
                data-testid="booking-add-included"
                className="bg-[#1DA3B3] hover:bg-[#178A98] text-white h-8"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Legg til inkludert produkt
              </Button>
            </div>
            {includedOrders.length === 0 ? (
              <p className="text-sm text-[#2B3F4D]">
                Ingen forhåndsbetalte produkter lagt til. Kunden ser kun
                tilleggsbestillinger som finnes her.
              </p>
            ) : (
              <div className="space-y-1.5">
                {includedOrders.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`admin-included-order-${o.id}`}
                    className="p-3 rounded-md border-l-4 border-l-[#1DA3B3] bg-[#1DA3B3]/5"
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <ul className="text-sm space-y-0.5 flex-1 min-w-0">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="flex justify-between text-[#2B3F4D]">
                            <span>× {it.quantity} {it.name_no}</span>
                            <span className="font-mono text-[#1DA3B3]">{it.line_total_eur} €</span>
                          </li>
                        ))}
                        <li className="flex justify-between font-semibold text-[#0C1824] pt-1.5 border-t border-[#1DA3B3]/20 mt-1.5">
                          <span>Sum (inkludert)</span>
                          <span className="font-mono">{o.subtotal_eur} €</span>
                        </li>
                      </ul>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeIncluded(o.id)}
                        data-testid={`admin-included-remove-${o.id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50 h-8"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {extraOrders.length > 0 && (
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
              Tilleggsbestillinger ({extraOrders.length})
            </div>
            {extraOrders.map((o) => (
              <div
                key={o.id}
                data-testid={`admin-order-${o.id}`}
                className="p-3 rounded-md border border-slate-200"
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <Badge className={
                      o.status === "paid"
                        ? "bg-emerald-100 text-emerald-700"
                        : o.status === "cancelled"
                        ? "bg-slate-200 text-slate-600"
                        : "bg-[#BD984D]/15 text-[#A57F39]"
                    }>
                      {o.status === "paid" ? "Betalt" : o.status === "cancelled" ? "Avbrutt" : "Venter"}
                    </Badge>
                    <ul className="text-sm mt-2 space-y-0.5">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="flex justify-between text-[#2B3F4D]">
                          <span>× {it.quantity} {it.name_no}</span>
                          <span className="font-mono text-[#A57F39]">{it.line_total_eur} €</span>
                        </li>
                      ))}
                      <li className="flex justify-between font-semibold text-[#0C1824] pt-1.5 border-t border-slate-100 mt-1.5">
                        <span>Sum</span>
                        <span className="font-mono">{o.subtotal_eur} €</span>
                      </li>
                    </ul>
                    {o.paid_at && (
                      <div className="text-[11px] text-[#2B3F4D] mt-1.5">
                        Betalt {new Date(o.paid_at).toLocaleString("nb-NO")}
                        {o.paid_by_name && ` · ${o.paid_by_name}`}
                        {o.payment_method && ` · ${o.payment_method}`}
                      </div>
                    )}
                  </div>
                  {o.status === "pending" && (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => markOrderPaid(o.id)}
                        data-testid={`admin-order-mark-paid-${o.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                      >
                        Marker betalt
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelPendingOrder(o.id, !!o.stripe_session_id)}
                        data-testid={`admin-order-cancel-${o.id}`}
                        className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                      >
                        Kanseller
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Editable fields */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-[#0C1824] mb-4">Detaljer</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Kundenavn</Label>
            <Input value={draft.customer_name || ""}
                   onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })}
                   data-testid="booking-edit-name" />
          </div>
          <div>
            <Label>Kunde e-post</Label>
            <Input type="email" value={draft.customer_email || ""}
                   onChange={(e) => setDraft({ ...draft, customer_email: e.target.value })}
                   data-testid="booking-edit-email" />
          </div>
          <div>
            <Label>Fra dato</Label>
            <Input type="date" value={draft.start_date || ""}
                   onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                   data-testid="booking-edit-start" />
          </div>
          <div>
            <Label>Til dato</Label>
            <Input type="date" value={draft.end_date || ""}
                   onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                   data-testid="booking-edit-end" />
          </div>
          <div>
            <Label>Antall personer</Label>
            <Input type="number" min={1} value={draft.num_guests || 1}
                   onChange={(e) => setDraft({ ...draft, num_guests: parseInt(e.target.value, 10) || 1 })}
                   data-testid="booking-edit-guests" />
          </div>
          <div>
            <Label>Båt</Label>
            <div className="text-sm text-[#0C1824] mt-2 flex items-center gap-1.5">
              <Anchor className="w-4 h-4 text-[#A57F39]" />
              {bk.boat?.name || "—"}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Label>Booking-notat (synlig for kunden)</Label>
          <Textarea rows={2} value={draft.notes || ""}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    placeholder="F.eks. spesielle ønsker, allergier, allmenn info kunden bør se"
                    data-testid="booking-edit-notes" />
        </div>

        {/* Custom check-in / -out times – overrides defaults (14:00 / 10:00) */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-2 font-semibold">
            Tilpassede klokkeslett (valgfritt)
          </div>
          <p className="text-xs text-[#2B3F4D] mb-3">
            La feltene stå tomme for standard: innsjekk fra <b>14:00</b>, utsjekk innen <b>10:00</b>.
            Fyll inn tid (HH:MM) hvis kunden har betalt for tidlig innsjekk eller sen utsjekk – kunden ser da avvikende tid direkte i portalen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Innsjekk (klokkeslett)</Label>
              <Input
                type="time"
                value={draft.checkin_time || ""}
                onChange={(e) => setDraft({ ...draft, checkin_time: e.target.value })}
                data-testid="booking-edit-checkin-time"
                placeholder="14:00"
                className="max-w-[180px]"
              />
              {draft.checkin_time && (
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, checkin_time: "" })}
                  className="text-xs text-[#A57F39] hover:text-red-600 mt-1"
                  data-testid="booking-edit-checkin-time-clear"
                >
                  Fjern (bruk standard 14:00)
                </button>
              )}
            </div>
            <div>
              <Label>Utsjekk (klokkeslett)</Label>
              <Input
                type="time"
                value={draft.checkout_time || ""}
                onChange={(e) => setDraft({ ...draft, checkout_time: e.target.value })}
                data-testid="booking-edit-checkout-time"
                placeholder="10:00"
                className="max-w-[180px]"
              />
              {draft.checkout_time && (
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, checkout_time: "" })}
                  className="text-xs text-[#A57F39] hover:text-red-600 mt-1"
                  data-testid="booking-edit-checkout-time-clear"
                >
                  Fjern (bruk standard 10:00)
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={saveFields} disabled={savingFields}
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
                  data-testid="booking-edit-save">
            <Save className="w-4 h-4 mr-1.5" />
            {savingFields ? "Lagrer…" : "Lagre endringer"}
          </Button>
        </div>
      </Card>

      {/* Self check-in / -out toggles + submission summary */}
      {isAdmin && bk.customer_user_id && (
        <SelfChecksAdminCard bookingId={id} bk={bk} onChanged={load} />
      )}

      {/* Time change requests from the customer (early check-in / late check-out) */}
      {isAdmin && bk.customer_user_id && (
        <AdminTimeChangeCard bookingId={id} onChanged={load} />
      )}

      {/* Customer-uploaded boat inspection photos (check-in / check-out) */}
      {bk.customer_user_id && (
        <AdminBoatInspectionCard bookingId={id} />
      )}

      {/* Faktura / kvittering per booking (admin only) */}
      {isAdmin && (
        <BookingPaymentsCard booking={bk} canEdit={isAdmin} />
      )}

      {/* Documents */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#0C1824]">Dokumenter</h2>
            <p className="text-xs text-[#2B3F4D] mt-1">
              Last opp PDF-er som kunden ser i sin portal. Globale dokumenter
              administreres under Innstillinger.
            </p>
          </div>
          {isAdmin && (
            <DocumentUploader scope="booking" bookingId={id} onUploaded={load} />
          )}
        </div>
        <div className="space-y-2">
          {(bk.documents || []).length === 0 && (
            <div className="text-sm text-[#2B3F4D] py-6 text-center border border-dashed border-slate-200 rounded-lg">
              Ingen dokumenter er lagt til på denne bookingen ennå.
            </div>
          )}
          {(bk.documents || []).map((d) => (
            <DocumentRow key={d.id} doc={d} canDelete={isAdmin} onDelete={deleteDoc} />
          ))}
        </div>
      </Card>

      {/* Internal notes (admin-only thread) */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-[#A57F39]" />
          <h2 className="text-lg font-semibold text-[#0C1824]">Interne oppdateringer</h2>
        </div>
        <p className="text-xs text-[#2B3F4D] mb-4">
          Kun synlig for admin og service. Kunden ser <em>ikke</em> disse notatene.
        </p>
        <div className="space-y-3" data-testid="booking-notes-list">
          {notes.length === 0 && (
            <div className="text-sm text-[#2B3F4D] italic">Ingen oppdateringer ennå.</div>
          )}
          {notes.slice().reverse().map((n) => (
            <div key={n.id} className="border-l-2 border-[#BD984D]/40 pl-3 py-1.5"
                 data-testid={`booking-note-${n.id}`}>
              <div className="text-sm text-[#0C1824] whitespace-pre-wrap">{n.body}</div>
              <div className="text-[11px] text-[#2B3F4D] mt-1 flex items-center gap-3">
                <span>{n.author_name} · {new Date(n.created_at).toLocaleString("nb-NO")}</span>
                {isAdmin && (
                  <button onClick={() => deleteNote(n.id)}
                          data-testid={`booking-note-delete-${n.id}`}
                          className="text-red-500 hover:underline text-[11px]">
                    Slett
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2 items-end">
          <div className="flex-1">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Skriv en oppdatering, f.eks. «Kunden ringte og ba om sen innsjekk kl 19.»"
              rows={2}
              data-testid="booking-note-input"
            />
          </div>
          <Button onClick={addNote} disabled={!newNote.trim()}
                  className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                  data-testid="booking-note-add">
            <Send className="w-4 h-4 mr-1.5" /> Legg til
          </Button>
        </div>
      </Card>

      {/* Dialog: move booking to a different boat (date overlap is detected
          server-side and surfaces as a non-blocking warning). */}
      <Dialog open={payLinkOpen} onOpenChange={setPayLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send betalingslink til kunden</DialogTitle>
            <DialogDescription>
              Lag først en betalingslenke i Stripe (buy.stripe.com/…), lim den inn her sammen
              med hva betalingen gjelder og beløpet i euro. Kunden ser en «Betal med kort»-knapp
              i portalen, og status oppdateres automatisk til «Betalt» når betalingen er utført.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Hva gjelder betalingen?</Label>
              <Input
                value={payLink.description}
                data-testid="paylink-description"
                onChange={(e) => setPayLink({ ...payLink, description: e.target.value })}
                placeholder="F.eks. Ekstra tank septik + skade på fender"
              />
            </div>
            <div>
              <Label>Beløp (€)</Label>
              <Input
                type="number" min={1} step="0.01"
                value={payLink.amount_eur}
                data-testid="paylink-amount"
                onChange={(e) => setPayLink({ ...payLink, amount_eur: e.target.value })}
                placeholder="F.eks. 150"
              />
              <p className="text-xs text-[#2B3F4D] mt-1">
                Må matche beløpet på Stripe-lenken (bekreftelse verifiserer beløpet).
              </p>
            </div>
            <div>
              <Label>Stripe-lenke</Label>
              <Input
                value={payLink.stripe_payment_url}
                data-testid="paylink-url"
                onChange={(e) => setPayLink({ ...payLink, stripe_payment_url: e.target.value })}
                placeholder="https://buy.stripe.com/…"
              />
              <p className="text-xs text-[#2B3F4D] mt-1">
                Husk å sette redirect på Stripe-lenken til
                <code className="mx-1 px-1 bg-slate-100 rounded">
                  https://canalboats.me/portal?stripe_session={"{CHECKOUT_SESSION_ID}"}
                </code>
                slik at kundens side vet at betaling er utført.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayLinkOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={submitPayLink}
              disabled={payLinkBusy}
              data-testid="paylink-submit"
              className="bg-[#635BFF] hover:bg-[#4F46E5] text-white"
            >
              <CreditCard className="w-4 h-4 mr-1" />
              Send til kunde
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Flytt booking til annen båt</DialogTitle>
            <DialogDescription>
              Velg ny båt for {bk.customer_name}. Systemet sjekker om
              den nye båten har overlappende booking i samme periode, men du kan
              fortsatt fullføre flyttingen om nødvendig.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-1">
                Nåværende båt
              </div>
              <div className="text-sm text-[#2B3F4D]">{bk.boat?.name || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-1.5">
                Ny båt
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {boats.filter((b) => !b.archived_at).map((b) => (
                  <label
                    key={b.id}
                    data-testid={`move-target-${b.id}`}
                    className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                      moveDraft.boat_id === b.id
                        ? "border-[#1DA3B3] bg-[#1DA3B3]/5"
                        : "border-slate-200 hover:border-[#1DA3B3]/40"
                    } ${b.id === bk.boat_id ? "opacity-50" : ""}`}
                  >
                    <input
                      type="radio"
                      name="move-boat"
                      checked={moveDraft.boat_id === b.id}
                      onChange={() => setMoveDraft((d) => ({ ...d, boat_id: b.id }))}
                      disabled={b.id === bk.boat_id}
                    />
                    <Anchor className="w-3.5 h-3.5 text-[#A57F39]" />
                    <span className="text-sm font-medium text-[#0C1824]">{b.name}</span>
                    {b.id === bk.boat_id && (
                      <span className="text-xs text-[#2B3F4D] ml-auto">(nåværende)</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-1">
                Årsak (valgfritt – logges i interne notater)
              </div>
              <Textarea
                rows={2}
                value={moveDraft.move_reason}
                onChange={(e) => setMoveDraft((d) => ({ ...d, move_reason: e.target.value }))}
                placeholder="F.eks. Greenline 39 ikke tilgjengelig, oppgraderer kunden …"
                data-testid="booking-move-reason"
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={moveBoat}
              disabled={movingBoat || !moveDraft.boat_id || moveDraft.boat_id === bk.boat_id}
              data-testid="booking-move-confirm"
              className="bg-[#1DA3B3] hover:bg-[#178A98] text-white"
            >
              {movingBoat ? "Flytter…" : "Flytt booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: add included-with-booking products */}
      <Dialog open={includedDialogOpen} onOpenChange={setIncludedDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Hva er inkludert i denne bookingen?</DialogTitle>
            <DialogDescription>
              Velg antall for hvert produkt kunden har forhåndsbetalt for. Kunden
              ser disse som «Inkludert i din booking» når de logger inn.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {products.length === 0 && (
              <p className="text-sm text-[#2B3F4D]">
                Ingen aktive produkter er definert. Opprett dem under
                Innstillinger → Produkter først.
              </p>
            )}
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`included-product-row-${p.id}`}
                className="flex items-center justify-between gap-3 p-2 rounded-md border border-slate-200"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0C1824]">{p.name_no}</div>
                  <div className="text-xs text-[#2B3F4D]">
                    {p.unit_price_eur} €
                    {p.type === "rental_per_day" && " · per natt"}
                    {p.type === "rental_per_booking" && " · per booking"}
                    {p.type === "purchase" && " · per enhet"}
                  </div>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={20}
                  step={1}
                  placeholder="0"
                  value={includedDraft[p.id] ?? ""}
                  onChange={(e) =>
                    setIncludedDraft((d) => ({ ...d, [p.id]: e.target.value }))
                  }
                  data-testid={`included-product-qty-${p.id}`}
                  className="h-9 w-20 text-right"
                />
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIncludedDialogOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={saveIncluded}
              disabled={savingIncluded || products.length === 0}
              data-testid="booking-included-save"
              className="bg-[#1DA3B3] hover:bg-[#178A98] text-white"
            >
              {savingIncluded ? "Lagrer…" : "Lagre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Self check-in / check-out admin card.
 * Lets admin toggle whether the customer can perform check-in/out themselves
 * (used when no host meets them physically) and shows every submitted answer.
 * ---------------------------------------------------------------------- */
function SelfChecksAdminCard({ bookingId, bk, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  const [inEn, setInEn] = useState(!!bk.self_checkin_enabled);
  const [outEn, setOutEn] = useState(!!bk.self_checkout_enabled);

  const reload = async () => {
    try {
      const { data } = await api.get(`/self-checks/${bookingId}`);
      setInEn(!!data.self_checkin_enabled);
      setOutEn(!!data.self_checkout_enabled);
      setSubs(data.submissions || []);
    } catch { /* noop */ }
  };
  useEffect(() => { reload(); }, [bookingId]);

  const toggle = async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${bookingId}/self-checks`, patch);
      await reload();
      onChanged?.();
      toast.success("Oppdatert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere");
    } finally {
      setBusy(false);
    }
  };

  const findSub = (kind) => subs.find((s) => s.kind === kind);
  const inSub = findSub("checkin");
  const outSub = findSub("checkout");

  return (
    <Card className="p-6" data-testid="booking-self-checks">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="w-5 h-5 text-[#A57F39]" />
        <h2 className="text-lg font-semibold text-[#0C1824]">Selv-innsjekk / -utsjekk</h2>
      </div>
      <p className="text-xs text-[#2B3F4D] mb-4 max-w-2xl">
        Skru på når du ikke har mulighet til å møte kunden. Kunden går da
        gjennom en digital sjekkliste (steg-for-steg med bilde-opplasting for
        skader). Innleveringen oppdaterer båtens status automatisk.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* -- check-in -- */}
        <div className="rounded-lg border border-[#BD984D]/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LogIn className="w-4 h-4 text-[#BD984D]" />
              <div className="font-semibold text-[#0C1824]">Selv-innsjekk</div>
            </div>
            <Switch
              data-testid="self-checkin-toggle"
              checked={inEn}
              disabled={busy || !!inSub}
              onCheckedChange={(v) => { setInEn(v); toggle({ self_checkin_enabled: v }); }}
            />
          </div>
          <div className="text-xs text-[#2B3F4D] mt-1">
            {inSub ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <ClipboardCheck className="w-3 h-3" />
                Levert {new Date(inSub.submitted_at).toLocaleString("nb-NO")}
              </span>
            ) : inEn ? (
              "Aktivert – venter på kunden."
            ) : (
              "Ikke aktivert. Kunden ser ingen sjekkliste."
            )}
          </div>
          {inSub && <SelfCheckAnswersList sub={inSub} />}
        </div>

        {/* -- check-out -- */}
        <div className="rounded-lg border border-[#1DA3B3]/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LogOut className="w-4 h-4 text-[#1DA3B3]" />
              <div className="font-semibold text-[#0C1824]">Selv-utsjekk</div>
            </div>
            <Switch
              data-testid="self-checkout-toggle"
              checked={outEn}
              disabled={busy || !!outSub}
              onCheckedChange={(v) => { setOutEn(v); toggle({ self_checkout_enabled: v }); }}
            />
          </div>
          <div className="text-xs text-[#2B3F4D] mt-1">
            {outSub ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <ClipboardCheck className="w-3 h-3" />
                Levert {new Date(outSub.submitted_at).toLocaleString("nb-NO")}
              </span>
            ) : outEn ? (
              "Aktivert – venter på kunden."
            ) : (
              "Ikke aktivert. Kunden ser ingen sjekkliste."
            )}
          </div>
          {outSub && <SelfCheckAnswersList sub={outSub} />}
        </div>
      </div>
    </Card>
  );
}

function SelfCheckAnswersList({ sub }) {
  const fmt = (item) => {
    switch (item.type) {
      case "check": return item.value ? "Bekreftet" : "—";
      case "yesno": return item.value === true ? "Ja" : item.value === false ? "Nei" : "—";
      case "count": return typeof item.value === "number" ? String(item.value) : "—";
      case "text": return item.text || "—";
      case "photo_report":
        if (item.text || (item.photo_ids && item.photo_ids.length)) {
          const parts = [];
          if (item.text) parts.push(item.text);
          if (item.photo_ids?.length) parts.push(`${item.photo_ids.length} bilde${item.photo_ids.length === 1 ? "" : "r"}`);
          return parts.join(" · ");
        }
        return "Ingen skader rapportert";
      case "info": return "OK";
      default: return "—";
    }
  };
  return (
    <details className="mt-3 text-sm" data-testid={`self-check-answers-${sub.kind}`}>
      <summary className="cursor-pointer text-[#A57F39] font-medium select-none">
        Se svar ({(sub.items || []).length})
      </summary>
      <ul className="mt-2 space-y-1 text-[#0C1824]">
        {(sub.items || []).map((it, idx) => (
          <li key={idx} className="flex gap-2 text-xs">
            <span className="text-slate-500 shrink-0 w-6 text-right">{idx + 1}.</span>
            <span className="font-mono text-[10px] text-slate-500 shrink-0">
              {it.key}
            </span>
            <span className="ml-auto text-right break-words">{fmt(it)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
